import {
  AgActionsMenuCellRendererComponent,
  AgActionsMenuItem,
} from 'src/app/ag-grid/components/cell-renderers/ag-actions-menu-cell-renderer/ag-actions-menu-cell-renderer.component';
import { Component, inject } from '@angular/core';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import {
  faDownload,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import {
  AgButtonCellRendererComponent,
  AgDateRangeFilterComponent,
} from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { AppState } from 'src/app/reducers';
import { AutoStoryPayload } from '../../services/feed.service';
import { AutoStoryToolDialogComponent } from '../../../shared/components/auto-story-tool-dialog/auto-story-tool-dialog.component';
import { ConfirmDialogComponent } from '../../components/popups/confirm-dialog/confirm-dialog.component';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { LoggerService } from '@services/logger.service';
import { ToastService } from '@services/toast.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { NomAlgorithmTaskStatus } from 'src/app/core/constants';
import { NomenclatureService } from '../../services/nomenclature.service';
import { FeedAlgorithmsService } from '../../services/feed-algorithms.service';
import { JsonViewerDialogComponent } from '../../components/popups/json-viewer-dialog/json-viewer-dialog.component';
import { selectUserAccessLevelGlobalAdmin } from 'src/app/core/reducers';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';
import { NomObjectClass } from 'src/app/core/constants';

@Component({
  selector: 'app-auto-story-runs',
  templateUrl: './auto-story-runs.component.html',
  standalone: false,
})
export class AutoStoryRunsComponent extends DataGridHostBase {
  private readonly userApi = inject(UserApiService);
  private readonly utilsService = inject(UtilsService);
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);
  private readonly dialogService = inject(DialogService);
  private readonly feedAlgorithms = inject(FeedAlgorithmsService);

  // ! stage_failure_output is only returned to global admins (access level 50+)
  private isGlobalAdmin = toSignal(
    inject(Store<AppState>).select(selectUserAccessLevelGlobalAdmin),
    { initialValue: false },
  );

  readonly viewId = ViewId.AutoStoryRuns;
  readonly title = this.translateService.instant(
    'auto-story-runs-grid.title.auto-story-runs',
  );
  readonly icon: IconProp = ['fas', 'wand-magic-sparkles'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.userApi.getUserAutoStoryRunsAgGrid(request);

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private deleteRun(data: any): void {
    const runId = data?.id;
    if (!runId) {
      this.toastService.showError(
        this.translateService.instant(
          'auto-story-runs.error.missing-object-id',
        ),
      );
      return;
    }

    const message = this.translateService.instant(
      'auto-story-runs.delete-popup.confirm-message',
      { id: runId ?? '' },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason !== DialogCloseReason.Confirm) {
          return;
        }

        this.userApi.deleteAutoStoryRun(runId).subscribe({
          next: (result) => {
            if (result?.success) {
              this.toastService.showSuccess(
                this.translateService.instant(
                  'auto-story-runs.success.run-deleted',
                ),
              );
              this.grid?.api?.refreshServerSide({ purge: true });
            } else {
              this.toastService.showError(
                this.translateService.instant(
                  'auto-story-runs.error.delete-run-failed',
                ),
              );
            }
          },
          error: (err) => {
            this.logger.error('Failed to delete auto story run', err);
            this.toastService.showError(
              this.translateService.instant(
                'auto-story-runs.error.delete-run-failed',
              ),
            );
          },
        });
      },
    );
  }

  private runAsNew(data: any): void {
    const initialPayload = this.extractPayloadFromRun(data);
    const feedId = data?.object?.id;
    const algorithmId = data?.algorithm?.id;

    if (!feedId || !algorithmId) {
      this.toastService.showError(
        this.translateService.instant(
          'auto-story-runs.error.missing-object-id',
        ),
      );
      return;
    }

    const dialogRef = this.dialogService.openSimpleDialog(
      AutoStoryToolDialogComponent,
      {
        data: { initialPayload, standalone: true },
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ payload: any } | undefined>) => {
        if (
          event.reason !== DialogCloseReason.Save &&
          event.reason !== DialogCloseReason.SaveAndClose
        ) {
          return;
        }

        this.feedAlgorithms.applyToFeedPosts(
          feedId,
          algorithmId,
          event.data?.payload ?? {},
          this.extractContentFiltersFromRun(data),
        );
      },
    );
  }

  private downloadResult(data: any): void {
    const url = data?.absolute_url;
    if (!url) return;

    window.open(url, '_blank', 'noopener');
  }

  private hasStageFailureOutput(data: any): boolean {
    const output = data?.stage_failure_output;
    if (!output) return false;

    return typeof output === 'object' ? Object.keys(output).length > 0 : true;
  }

  private openStageFailureOutputDialog(data: any): void {
    if (!this.hasStageFailureOutput(data)) return;

    this.dialogService.openSimpleDialog(JsonViewerDialogComponent, {
      data: {
        title: this.translateService.instant(
          'auto-story-runs.dialog.stage-failure-output',
        ),
        json: data.stage_failure_output,
      },
      closeOnClick: false,
      closeOnEscape: true,
      width: '40%',
      height: '70%',
      minWidth: 500,
      minHeight: 800,
      maxWidth: 1020,
      maxHeight: 1220,
    });
  }

  // * the original run's content filters (locked time period, active filters) are
  // * persisted in its request params; replaying them keeps the dataset identical
  private extractContentFiltersFromRun(data: any): any {
    const raw = data?.params ?? {};
    return {
      time_start: raw.time_start,
      time_end: raw.time_end,
      filters: raw.filters ?? [],
    };
  }

  private extractPayloadFromRun(data: any): Partial<AutoStoryPayload> {
    const raw = data?.params ?? {};
    return {
      prompt_template_id: raw.prompt_template_id,
      prompt_params: raw.prompt_params,
      model: raw.model,
      clusters: raw.clusters,
      distance_threshold: raw.distance_threshold,
      sanitize_mentions: raw.sanitize_mentions,
      sanitize_urls: raw.sanitize_urls,
      sort: raw.sort,
      sample_metric: raw.sample_metric,
      sampling_percentile_groups: raw.sampling_percentile_groups,
      sampling_percentage_per_group: raw.sampling_percentage_per_group,
      sampling_time_buckets: !!raw.sampling_time_buckets,
      sample_length: raw.sample_length,
      create_story_posts: raw.create_story_posts ?? true,
      send_notification: raw.send_notification ?? true,
    };
  }

  private buildColumnDefs(): ColDef[] {
    const rowActions: AgActionsMenuItem[] = [
      {
        label: this.translateService.instant(
          'auto-story-runs.action.delete-run',
        ),
        icon: ['fas', 'trash'],
        action: (data) => this.deleteRun(data),
      },
      {
        label: this.translateService.instant(
          'auto-story-runs.action.run-as-new',
        ),
        icon: ['fas', 'rotate-right'],
        action: (data) => this.runAsNew(data),
      },
    ];

    const columnDefs: ColDef[] = [
      {
        headerName: this.translateService.instant('grid.column.id'),
        field: 'id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 80,
        maxWidth: 80,
        pinned: 'left',
        cellRenderer: AgActionsMenuCellRendererComponent,
        cellRendererParams: {
          titleKey: 'id',
          items: rowActions,
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.company'),
        field: 'company.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 200,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'company',
          clickAccessLevel:
            OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
          onClick: (company) => this.objectEditor.editCompany(company?.id),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.object-class'),
        field: 'object.class_name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 120,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.object-type'),
        field: 'object.type_name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 140,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.object'),
        field: 'object.id',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 220,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'object',
          // Only refs the dispatcher can open (a feed today) are links.
          clickable: (ref) => this.objectEditor.canOpenDataObjectRef(ref),
          onClick: (ref) => this.objectEditor.openDataObjectRef(ref),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.user'),
        field: 'user.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 200,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'user',
          clickAccessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
          onClick: (user) => this.objectEditor.editUser(user.id),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.user-email'),
        field: 'user.email',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 220,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.is-scheduled'),
        field: 'is_scheduled',
        sortable: true,
        minWidth: 110,
        maxWidth: 140,
        filterParams: {
          values: ['true', 'false'],
        },
        cellRenderer: AgBooleanCellRendererComponent,
        cellRendererParams: {
          isActive: 'data.is_scheduled',
        },
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-run-id',
        ),
        field: 'schedule_run_id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.time-started'),
        field: 'time_created.absolute',
        filter: AgDateRangeFilterComponent,
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
        minWidth: 180,
      },
      {
        headerName: this.translateService.instant('grid.column.time-modified'),
        field: 'time_modified.absolute',
        filter: AgDateRangeFilterComponent,
        sortable: true,
        sort: 'desc',
        cellRenderer: AgDateCellRendererComponent,
        minWidth: 180,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.time-elapsed'),
        field: 'time_elapsed',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 100,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.task-status',
            fieldKey: 'task_status.id',
            fieldValue: 'task_status.name',
            nomenclatureName: 'algorithm_task_status',
          },
        ),
        sortable: true,
        minWidth: 100,
        cellStyle: (params) => {
          const style = { fontWeight: 'var(--font-weight-medium)' };

          switch (params?.data?.task_status?.id) {
            case NomAlgorithmTaskStatus.FAILED:
              return { ...style, color: 'var(--color-feedback-danger-fg)' };
            case NomAlgorithmTaskStatus.COMPLETED:
              return { ...style, color: 'var(--color-feedback-success-fg)' };
            default:
              return style;
          }
        },
      },
      {
        headerName: this.translateService.instant('grid.column.email-status'),
        field: 'email_status.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 120,
      },
      {
        headerName: this.translateService.instant('grid.column.download'),
        field: 'absolute_url',
        filter: false,
        sortable: false,
        minWidth: 90,
        maxWidth: 110,
        valueFormatter: () => '',
        cellRendererSelector: (params) => {
          if (!params?.data?.absolute_url) return undefined;

          return {
            component: AgButtonCellRendererComponent,
            params: {
              variant: 'secondary',
              leadingIcon: faDownload,
              iconOnly: true,
              ariaLabel: this.translateService.instant(
                'auto-story-runs.action.download-result',
              ),
              action: this.downloadResult.bind(this),
            },
          };
        },
      },
      {
        headerName: this.translateService.instant(
          'grid.column.stage-failure-output',
        ),
        field: 'stage_failure_output',
        filter: false,
        sortable: false,
        minWidth: 110,
        maxWidth: 130,
        hide: !this.isGlobalAdmin(),
        valueFormatter: () => '',
        cellRendererSelector: (params) => {
          // * no failure payload — the run cleared every stage
          if (!this.hasStageFailureOutput(params?.data)) {
            return {
              component: AgBooleanCellRendererComponent,
              params: { isActive: true },
            };
          }

          return {
            component: AgButtonCellRendererComponent,
            params: {
              variant: 'danger',
              leadingIcon: faExclamationTriangle,
              iconOnly: true,
              ariaLabel: this.translateService.instant(
                'auto-story-runs.dialog.stage-failure-output',
              ),
              action: this.openStageFailureOutputDialog.bind(this),
            },
          };
        },
      },
      {
        headerName: this.translateService.instant('grid.column.algorithm'),
        field: 'algorithm.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 160,
        initialHide: true,
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.stage',
            fieldKey: 'stage.id',
            fieldValue: 'stage.name',
            nomenclatureName: 'algorithm_stage',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        sortable: true,
        minWidth: 140,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.progress-percent',
        ),
        field: 'progress_percent',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 100,
        type: 'rightAligned',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.data-set-items-count',
        ),
        field: 'dataset_items_count',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant('grid.column.data-set-size'),
        field: 'dataset_size_bytes',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 140,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant(
          'grid.column.summarized-size',
        ),
        field: 'summarized_items_size_bytes',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 150,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.sampling-savings',
        ),
        field: 'dataset_savings_percent',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 120,
        type: 'rightAligned',
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) + '%' : '',
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.llm-vendor',
            fieldKey: 'llm_vendor.id',
            fieldValue: 'llm_vendor.name',
            nomenclatureName: 'llm_vendor',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        sortable: true,
        minWidth: 110,
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.llm-model',
            fieldKey: 'llm_model.id',
            fieldValue: 'llm_model.name',
            nomenclatureName: 'llm_model',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        sortable: true,
        minWidth: 110,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-llm-batches',
        ),
        field: 'total_llm_batches',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 110,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-llm-failures',
        ),
        field: 'total_llm_failures',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 110,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-prompt-tokens',
        ),
        field: 'total_prompt_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-payload-tokens',
        ),
        field: 'total_payload_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 170,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-input-tokens',
        ),
        field: 'total_input_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 150,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-output-tokens',
        ),
        field: 'total_output_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.total-tokens'),
        field: 'total_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-cached-tokens',
        ),
        field: 'total_cached_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant('grid.column.total-cost'),
        field: 'total_cost',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (params) =>
          params.value != null ? '$' + Number(params.value).toFixed(4) : '',
      },
      {
        headerName: this.translateService.instant('grid.column.total-price'),
        field: 'total_price',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (params) =>
          params.value != null ? '$' + Number(params.value).toFixed(4) : '',
      },
      {
        headerName: this.translateService.instant('grid.column.failed-stage'),
        field: 'failed_stage.id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 140,
        type: 'rightAligned',
        initialHide: true,
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
    ];

    return columnDefs;
  }
}
